import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Filter, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ContactForm from '../components/ContactForm';
import { formatDistanceToNow } from 'date-fns';

function Contacts() {
  const {
    contacts,
    loading,
    fetchContacts,
    createContact,
    categories,
    fetchCategories,
    tags,
    fetchTags
  } = useApp();

  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchContacts();
    fetchCategories();
    fetchTags();
  }, []); // Only fetch once on mount

  const handleSearch = useCallback((value) => {
    setSearch(value);
    fetchContacts({ search: value, category: categoryFilter, tag: tagFilter });
  }, [categoryFilter, tagFilter]); // fetchContacts is stable from context

  const handleCategoryChange = (value) => {
    setCategoryFilter(value);
    fetchContacts({ search, category: value, tag: tagFilter });
  };

  const handleTagChange = (value) => {
    setTagFilter(value);
    fetchContacts({ search, category: categoryFilter, tag: value });
  };

  const handleSave = async (data) => {
    await createContact(data);
    fetchContacts({ search, category: categoryFilter, tag: tagFilter });
  };

  const getCategoryColor = (category) => {
    const colors = {
      family: '#ef4444',
      friends: '#22c55e',
      professional: '#3b82f6',
      acquaintance: '#6b7280'
    };
    return colors[category] || '#6b7280';
  };

  const getStrengthColor = (strength) => {
    if (strength >= 70) return 'var(--success)';
    if (strength >= 40) return 'var(--warning)';
    return 'var(--danger)';
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
            Contacts
          </h1>
          <p className="text-sm text-muted">
            {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} />
          Add Contact
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="search-wrapper" style={{ flex: 1, minWidth: '200px' }}>
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="form-input search-input"
              placeholder="Search contacts..."
            />
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            Filters
            {(categoryFilter || tagFilter) && (
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--primary)',
                marginLeft: '0.25rem'
              }} />
            )}
          </button>
        </div>

        {showFilters && (
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--gray-200)',
            flexWrap: 'wrap'
          }}>
            <div style={{ minWidth: '150px' }}>
              <label className="form-label">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="form-input form-select"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name.toLowerCase()}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: '150px' }}>
              <label className="form-label">Tag</label>
              <select
                value={tagFilter}
                onChange={(e) => handleTagChange(e.target.value)}
                className="form-input form-select"
              >
                <option value="">All Tags</option>
                {tags.map(tag => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>
            {(categoryFilter || tagFilter) && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setCategoryFilter('');
                  setTagFilter('');
                  fetchContacts({ search });
                }}
                style={{ alignSelf: 'flex-end' }}
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Contacts List */}
      {loading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="card empty-state">
          <p>No contacts found</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => setShowForm(true)}
          >
            Add Your First Contact
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="contacts-table-view">
            <div className="card" style={{ padding: 0 }}>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Category</th>
                      <th className="mobile-hide">Company</th>
                      <th className="mobile-hide">Strength</th>
                      <th className="mobile-hide">Last Contact</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(contact => (
                      <tr key={contact.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div className="avatar">
                              {contact.first_name[0]}{contact.last_name?.[0] || ''}
                            </div>
                            <div>
                              <div className="font-medium">
                                {contact.first_name} {contact.last_name}
                              </div>
                              {contact.email && (
                                <div className="text-sm text-muted">{contact.email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              background: `${getCategoryColor(contact.category)}15`,
                              color: getCategoryColor(contact.category),
                              textTransform: 'capitalize'
                            }}
                          >
                            {contact.category}
                          </span>
                        </td>
                        <td className="mobile-hide">
                          {contact.company && (
                            <div>
                              <div className="font-medium">{contact.company}</div>
                              {contact.job_title && (
                                <div className="text-sm text-muted">{contact.job_title}</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="mobile-hide">
                          <div style={{ width: '80px' }}>
                            <div className="text-sm font-medium" style={{ marginBottom: '0.25rem' }}>
                              {contact.relationship_strength}%
                            </div>
                            <div className="strength-bar">
                              <div
                                className="strength-fill"
                                style={{
                                  width: `${contact.relationship_strength}%`,
                                  background: getStrengthColor(contact.relationship_strength)
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="mobile-hide">
                          {contact.last_interaction_date ? (
                            <span className="text-sm">
                              {formatDistanceToNow(new Date(contact.last_interaction_date), { addSuffix: true })}
                            </span>
                          ) : (
                            <span className="text-sm text-muted">Never</span>
                          )}
                        </td>
                        <td>
                          <Link
                            to={`/contacts/${contact.id}`}
                            className="btn btn-ghost btn-sm"
                          >
                            <ChevronRight size={18} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="contacts-card-view">
            {contacts.map(contact => (
              <Link
                key={contact.id}
                to={`/contacts/${contact.id}`}
                className="contact-card"
              >
                <div className="avatar">
                  {contact.first_name[0]}{contact.last_name?.[0] || ''}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="font-medium">
                    {contact.first_name} {contact.last_name}
                  </div>
                  {contact.company && (
                    <div className="text-sm text-muted" style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {contact.company}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', alignItems: 'center' }}>
                    <span
                      className="badge"
                      style={{
                        background: `${getCategoryColor(contact.category)}15`,
                        color: getCategoryColor(contact.category),
                        textTransform: 'capitalize',
                        fontSize: '0.6875rem'
                      }}
                    >
                      {contact.category}
                    </span>
                    <span className="text-xs text-muted">
                      {contact.relationship_strength}%
                    </span>
                  </div>
                </div>
                <ChevronRight size={18} className="text-muted" />
              </Link>
            ))}
          </div>

          <style>{`
            .contacts-table-view {
              display: block;
            }
            .contacts-card-view {
              display: none;
            }
            @media (max-width: 768px) {
              .contacts-table-view {
                display: none;
              }
              .contacts-card-view {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
              }
              .contact-card {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                padding: 0.75rem;
                background: white;
                border-radius: 0.5rem;
                text-decoration: none;
                color: inherit;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              }
              .contact-card:hover {
                background: var(--gray-50);
              }
            }
          `}</style>
        </>
      )}

      {showForm && (
        <ContactForm
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

export default Contacts;
